/**
 * graphFilterLogic - the pure decisions behind the Workbench graph filter
 * effect (package 8, canvas honesty).
 *
 * The effect in pages/Workbench.tsx owns the stash/restore plumbing; the
 * decisions live here so they can be tested without a canvas:
 *
 *   - whether the intermediate (Tier-3) filter is engaged at all,
 *   - which tier a node falls in,
 *   - whether the risk chips and "High risk only" show a node, with
 *     never-assessed nodes handled as their own "not assessed" group instead
 *     of being treated as score 0.
 */
import { CLUSTER_AUTO_THRESHOLD_NODES } from './placementClusters';
import { isTier1EntityType, riskBucket } from './canvasAssurance';

/**
 * Above this many nodes the cluster folding takes over (utils/placementClusters),
 * so the canvas is already condensing; the Tier-3 filter then stays on by
 * default. At or below it, "All nodes" is the default after a run.
 */
export const TIER_DENSITY_THRESHOLD = CLUSTER_AUTO_THRESHOLD_NODES;

export interface TierFilterFlags {
  showIntermediateNodes: boolean;
  showAllNodes: boolean;
  /** True once the user has picked an intermediaries / all-nodes mode. */
  tierFilterChosen?: boolean;
}

/** Is the Tier-3 (intermediate wallets) filter in force? */
export function isTierFilterEngaged(
  f: TierFilterFlags,
  investigationRootId: string | null | undefined,
  nodeCount: number,
): boolean {
  if (f.showAllNodes || !investigationRootId) return false;
  return !!f.tierFilterChosen || nodeCount > TIER_DENSITY_THRESHOLD;
}

/** Is "All nodes" what the viewer is effectively seeing? (for chip state) */
export function isAllNodesEffective(f: TierFilterFlags, nodeCount: number): boolean {
  return f.showAllNodes || (!f.tierFilterChosen && nodeCount <= TIER_DENSITY_THRESHOLD);
}

export interface TierContext {
  rootId: string;
  goldPathNodes: ReadonlySet<string>;
  hop1Neighbors: ReadonlySet<string>;
}

/** 1 = always shown, 2 = hop-1 neighbour, 3 = intermediate. */
export function classifyFilterTier(id: string, nd: any, ctx: TierContext): 1 | 2 | 3 {
  const classes: string[] = Array.isArray(nd?.investigation_classes) ? nd.investigation_classes : [];
  if (id === ctx.rootId) return 1;
  if (nd?.entity_type && isTier1EntityType(nd.entity_type)) return 1;
  if (ctx.goldPathNodes.has(id)) return 1;
  if (classes.includes('top-counterparty')) return 1;
  if (classes.includes('escalate-node') || classes.includes('investigate-node')) return 1;
  if (Array.isArray(nd?.intelligence_attributions)
      && nd.intelligence_attributions.some((a: any) => (a?.confidence || 0) >= 0.6)) return 1;
  if (ctx.hop1Neighbors.has(id)) return 2;
  return 3;
}

/** Does the tier filter hide a node of this tier? */
export function tierHides(tier: number, f: TierFilterFlags): boolean {
  if (!f.showIntermediateNodes && tier >= 2) return true; // hide Tier 2 + 3
  if (f.showIntermediateNodes && !f.showAllNodes && tier >= 3) return true; // hide Tier 3 only
  return false;
}

export interface RiskFilterFlags {
  riskLevels: ReadonlySet<string>;
  /** The separate "not assessed" chip. Undefined counts as shown. */
  showNotAssessed?: boolean;
  showOnlyHighRisk: boolean;
}

/**
 * Risk chips: assessed nodes follow the four level chips; never-assessed nodes
 * follow the "not assessed" chip. Unscored is not "low".
 */
export function passesRiskLevelFilter(d: any, f: RiskFilterFlags): boolean {
  const bucket = riskBucket(d);
  if (bucket === 'not_assessed') return f.showNotAssessed !== false;
  return f.riskLevels.has(bucket);
}

/**
 * "High risk only": assessed nodes must be high or critical. Never-assessed
 * nodes are not scored 0 and dropped; they stay governed by the "not assessed"
 * chip, so the investigator sees the count of what could not be judged.
 */
export function passesHighRiskOnly(d: any, f: RiskFilterFlags): boolean {
  const bucket = riskBucket(d);
  if (bucket === 'not_assessed') return f.showNotAssessed !== false;
  return bucket === 'high' || bucket === 'critical';
}

/**
 * Edge-click routing: which Transfers-tab selection a click on an edge sets.
 * Returns null for edges with no real transactions (synthetic links).
 */
export function resolveEdgeClickSelection(
  edge: any,
): { pair: { source: string; target: string }; clearEdgeIds: boolean } | null {
  if (!edge) return null;
  if (edge.is_synthetic || edge.edgeType === 'synthetic-link') return null;
  if (!edge.source || !edge.target) return null;
  // Consolidated and gold-path edges reset the multi-select list; a plain
  // edge keeps the id the canvas already put there (its own selection).
  const consolidated = !!edge.is_consolidated || edge.edgeType === 'gold-path';
  return { pair: { source: edge.source, target: edge.target }, clearEdgeIds: consolidated };
}

/**
 * Tier of every node in the filter universe: gold-path nodes from the edge
 * data, hop-1 neighbours of the investigation root from the adjacency, then
 * `classifyFilterTier` per node.
 */
export function computeTierMap(
  nodeUniverse: ReadonlyArray<{ id: string; data?: any }>,
  edgeUniverse: ReadonlyArray<{ data?: any }>,
  adjacency: ReadonlyMap<string, ReadonlySet<string>>,
  rootId: string,
): Map<string, number> {
  const goldPathNodes = new Set<string>();
  for (const edge of edgeUniverse) {
    const d = edge.data || {};
    const classes: string[] = Array.isArray(d.investigation_classes) ? d.investigation_classes : [];
    if (classes.includes('gold-path') || d.edgeType === 'gold-path') {
      if (d.source) goldPathNodes.add(d.source);
      if (d.target) goldPathNodes.add(d.target);
    }
  }
  const hop1Neighbors = new Set<string>(adjacency.get(rootId) ?? []);
  hop1Neighbors.delete(rootId);
  const tierMap = new Map<string, number>();
  for (const n of nodeUniverse) {
    tierMap.set(n.id, classifyFilterTier(n.id, n.data || {}, { rootId, goldPathNodes, hop1Neighbors }));
  }
  return tierMap;
}
